import type { IconType } from 'react-icons';
import {
  FiTarget,
  FiLayers,
  FiCheckSquare,
  FiCalendar,
  FiTruck,
  FiUsers,
  FiUser,
  FiTool,
  FiBriefcase,
  FiPackage,
  FiClipboard,
  FiCreditCard,
  FiFileText,
  FiDollarSign,
  FiBarChart2,
  FiGrid,
  FiActivity,
  FiEye,
  FiShield,
  FiLock,
  FiZap,
  FiDownload,
} from 'react-icons/fi';

/**
 * Single source of truth for everything the marketing site claims about the
 * product. The home page and the pricing page both read from here, so a module
 * or a price can never be listed one way in one place and another way in another.
 */

export interface ModuleItem {
  name: string;
  desc: string;
  icon: IconType;
}

export interface ModuleGroup {
  group: string;
  tag: string;
  /** Tailwind gradient stops for the group's accent. */
  gradient: string;
  items: ModuleItem[];
}

/**
 * All 16 shipped modules, clustered the way a firm actually thinks about its
 * work rather than the way the database is laid out.
 */
export const moduleGroups: ModuleGroup[] = [
  {
    group: 'Sales & CRM',
    tag: 'Win more work',
    gradient: 'from-violet-500 to-primary-500',
    items: [
      { name: 'Leads', desc: 'Capture every enquiry and track it through your pipeline to won.', icon: FiTarget },
      { name: 'Projects', desc: 'Each project from kickoff to handover, with budget and spend in one view.', icon: FiLayers },
    ],
  },
  {
    group: 'Project Delivery',
    tag: 'Ship on schedule',
    gradient: 'from-primary-500 to-accent-500',
    items: [
      { name: 'Tasks', desc: 'Assign, track and close studio and site work with clear ownership.', icon: FiCheckSquare },
      { name: 'Attendance', desc: 'Daily site attendance for your whole crew, ready for payroll.', icon: FiCalendar },
      { name: 'Equipment', desc: 'Owned and rented machinery with rates, deposits and contract refs.', icon: FiTruck },
    ],
  },
  {
    group: 'Workforce',
    tag: 'Manage your people',
    gradient: 'from-accent-500 to-teal-500',
    items: [
      { name: 'Workers', desc: 'Day-rate site labour, always linked to the project they worked on.', icon: FiUsers },
      { name: 'Employees', desc: 'Your own salaried staff, departments and payroll details.', icon: FiUser },
      { name: 'Contractors', desc: 'Outside firms and subcontracted packages, tracked separately.', icon: FiTool },
    ],
  },
  {
    group: 'Procurement',
    tag: 'Control your spend',
    gradient: 'from-amber-500 to-orange-500',
    items: [
      { name: 'Vendors', desc: 'Suppliers with a full 360° history of orders, bills and spend.', icon: FiBriefcase },
      { name: 'Inventory', desc: 'Stock levels across sites and stores, with low-stock visibility.', icon: FiPackage },
      { name: 'Purchase Orders', desc: 'Raise, send and receive POs with line items and totals.', icon: FiClipboard },
      { name: 'Vendor Bills', desc: 'What you owe suppliers, raised automatically when a PO is received.', icon: FiCreditCard },
    ],
  },
  {
    group: 'Finance',
    tag: 'See the money clearly',
    gradient: 'from-emerald-500 to-accent-500',
    items: [
      { name: 'Invoices', desc: 'Bill clients, track what is paid and what is still outstanding.', icon: FiFileText },
      { name: 'Payments', desc: 'Every payment with mode, UPI ID, bank reference and cheque details.', icon: FiDollarSign },
      { name: 'Reports', desc: '15+ built-in reports with every total computed at the source.', icon: FiBarChart2 },
      { name: 'Dashboard', desc: 'Live rollups of projects, cash position and team activity.', icon: FiGrid },
    ],
  },
];

export const moduleCount = moduleGroups.reduce((sum, g) => sum + g.items.length, 0);

/** The three things that make this an ERP rather than a folder of CRUD screens. */
export interface Pillar {
  title: string;
  kicker: string;
  desc: string;
  icon: IconType;
  points: string[];
}

export const pillars: Pillar[] = [
  {
    kicker: 'One ledger',
    title: 'Your numbers reconcile themselves',
    desc:
      'Purchase orders, client invoices, vendor bills, equipment rental and payroll all post to a single money ledger. Profit and loss, and spend per project, are always the real figures.',
    icon: FiActivity,
    points: [
      'A purchase order books its cost the moment goods are received',
      'Supplier bills stay out of your revenue, so earnings are never inflated',
      'Delete an order and the committed spend comes straight back out',
    ],
  },
  {
    kicker: '360° views',
    title: 'Open a record, see the whole story',
    desc:
      'Every project, worker and vendor has a single page that pulls in every other module touching it. No more opening six screens to answer one question.',
    icon: FiEye,
    points: [
      'Project view: tasks, workers, attendance, POs, invoices and spend',
      'Vendor view: complete order history and total spend to date',
      'Worker view: attendance, projects worked and payments made',
    ],
  },
  {
    kicker: 'Audit trail',
    title: 'Know who changed what, and when',
    desc:
      'Every module writes to an activity log, so each record carries its own timeline. When a number looks wrong, you can see exactly how it got that way.',
    icon: FiShield,
    points: [
      'Created, updated and deleted events on every record',
      'Field-level diffs, not just a vague "was edited" line',
      'Status changes tracked as first-class events',
    ],
  },
];

/** Smaller trust points for the strip under the pillars. */
export const capabilities: { label: string; desc: string; icon: IconType }[] = [
  { label: 'Isolated by design', desc: 'Every firm gets its own database schema. Your data is never mixed with anyone else\'s.', icon: FiLock },
  { label: 'Role-based access', desc: 'Admin, manager, accountant and viewer roles, enforced on the server too.', icon: FiShield },
  { label: 'Built for India', desc: 'Rupee-first throughout, with GST on invoices and UPI, NEFT and cheque payment modes.', icon: FiZap },
  { label: 'Your data, exportable', desc: 'Every report and register exports to PDF and Excel whenever you want it.', icon: FiDownload },
];

export interface Plan {
  id: 'basic' | 'pro' | 'enterprise';
  name: string;
  /** Rupees per month on the monthly cycle. Matches PlanType in the backend. */
  monthly: number;
  tagline: string;
  seats: string;
  popular?: boolean;
  /** Carried-forward line, e.g. "Everything in Pro, plus". */
  inherits?: string;
  features: string[];
  cta: string;
}

/**
 * Prices mirror the backend PlanType enum (basic ₹999, pro ₹2499,
 * enterprise ₹4999 per month). Annual billing is ten months for twelve.
 */
export const plans: Plan[] = [
  {
    id: 'basic',
    name: 'Basic',
    monthly: 999,
    tagline: 'For solo architects and small studios getting organised for the first time.',
    seats: 'Up to 5 team members',
    features: [
      'Leads and project management',
      'Tasks with owners and due dates',
      'Client invoicing with GST',
      'Site attendance and day-rate workers',
      'Core dashboard and 5 reports',
      'Email support',
    ],
    cta: 'Get started',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthly: 2499,
    popular: true,
    tagline: 'For growing firms that want procurement and finance joined up end to end.',
    seats: 'Up to 25 team members',
    inherits: 'Everything in Basic, plus',
    features: [
      'Full procurement suite — vendors, inventory, POs and vendor bills',
      'Equipment and rental cost tracking',
      'The unified money ledger and project spend',
      '360° project, worker and vendor views',
      'All 15+ built-in reports with PDF and Excel export',
      'Priority support',
    ],
    cta: 'Get started',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 4999,
    tagline: 'For established practices that need scale, control and a defensible audit trail.',
    seats: 'Unlimited team members',
    inherits: 'Everything in Pro, plus',
    features: [
      'Complete audit trail and activity log on every record',
      'Contractors, employees and payroll',
      'Advanced role-based permissions',
      'Dedicated onboarding and data migration',
      'Named account manager',
      'Custom reports on request',
    ],
    cta: 'Talk to us',
  },
];

/** Annual billing gives two months free, so twelve months cost ten. */
export const ANNUAL_MONTHS_CHARGED = 10;

export const annualPrice = (plan: Plan) => plan.monthly * ANNUAL_MONTHS_CHARGED;

/** Effective per-month cost when paying annually, rounded to whole rupees. */
export const annualPerMonth = (plan: Plan) => Math.round(annualPrice(plan) / 12);

export const formatINR = (value: number) => value.toLocaleString('en-IN');

export const faqs: { q: string; a: string }[] = [
  {
    q: 'Can I try BuildFlow before paying?',
    a: 'Yes. Tell us about your firm and we will set up a workspace with your own isolated data so you can walk through every module with your real projects. No card needed to start.',
  },
  {
    q: 'What happens when I outgrow my plan?',
    a: 'Request an upgrade from inside your profile and we move you across. Your data stays exactly where it is — nothing is re-imported and nothing is lost.',
  },
  {
    q: 'Is my firm\'s data separated from other customers?',
    a: 'Completely. Each company is provisioned its own database schema rather than sharing tables with a tenant column, so one firm\'s records are never in the same table as another\'s.',
  },
  {
    q: 'Do you charge per module?',
    a: 'No. Your plan unlocks a set of modules and there are no per-module add-on fees. The price you see is the price you pay, plus GST.',
  },
  {
    q: 'Can I get my data out?',
    a: 'Always. Every register and all 15+ reports export to PDF and Excel on demand, and we will hand over a full dump of your schema if you ever leave.',
  },
  {
    q: 'Does it work on site, on a phone?',
    a: 'Yes. The whole dashboard is responsive, so marking attendance or checking a purchase order from a phone on site works the same as from the studio.',
  },
];
